import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";

const DEFAULT_TEXT_SIZING = {
  minWidth: 120,
  maxWidth: 240,
  characterWidth: 8,
  horizontalPadding: 32,
};

const TYPE_SIZING = {
  date: { width: 125 },
  autocomplete: { width: 120 },
  number: { width: 120 },
};

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), maximum);

const isNumericValue = (value) => {
  const normalized = String(value ?? "").replace(/,/g, "").trim();
  return normalized !== "" && /^[-+]?\d+(\.\d+)?$/.test(normalized);
};

// Calculates widths from the data without measuring DOM nodes. Text columns
// grow only until their configured cap, so one long value cannot distort the
// table; numeric/date/status columns remain compact and predictable.
export const getTableColumnWidths = ({
  columns,
  rows,
  getValue,
}) =>
  columns.reduce((widths, column) => {
    const sizing = TYPE_SIZING[column.type];

    if (sizing?.width) {
      widths[column.key] = sizing.width;
      return widths;
    }

    const values = (rows || []).map((row) => getValue(row, column.key));
    if (values.length && values.every(isNumericValue)) {
      widths[column.key] = TYPE_SIZING.number.width;
      return widths;
    }

    const longestValueLength = values.reduce((longest, value) => {
      return Math.max(longest, String(value ?? "").length);
    }, String(column.name || "").length);

    widths[column.key] = clamp(
      longestValueLength * DEFAULT_TEXT_SIZING.characterWidth +
        DEFAULT_TEXT_SIZING.horizontalPadding,
      DEFAULT_TEXT_SIZING.minWidth,
      DEFAULT_TEXT_SIZING.maxWidth,
    );
    return widths;
  }, {});

// Keeps dense rows single-line while preserving the complete value on hover
// and keyboard focus. The same component can be used by any list screen.
export const TruncatedTableValue = ({ value, children }) => {
  const displayValue = String(value ?? "N/A");
  const valueRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = valueRef.current;
    if (!element) return undefined;

    const checkTruncation = () => {
      const contentElement =
        element.querySelector("[data-table-value-content]") || element;
      setIsTruncated(contentElement.scrollWidth > contentElement.clientWidth);
    };

    checkTruncation();
    const animationFrame = window.requestAnimationFrame(checkTruncation);
    // ResizeObserver is missing in some test environments (jsdom).
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(checkTruncation);
    observer?.observe(element);
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [displayValue]);

  return (
    <Tooltip
      title={displayValue}
      arrow
      slotProps={tooltipStyleProps}
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
    >
      <Box
        ref={valueRef}
        sx={{
          display: "block",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {children || displayValue}
      </Box>
    </Tooltip>
  );
};
